import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Button, Select, message, WkdUploadCore, Chips } from '@common/musicHubCommon';
import { useUserStore, useGenreStore, useMoodStore } from '../../../store';  
import './index.scss';

const COUNTRIES = [
  { label: 'Nigeria', value: 'NG' },
  { label: 'Tanzania', value: 'TZ' },
  { label: 'Uganda', value: 'UG' },
  { label: 'Côte d\'Ivoire', value: 'CI' },
  { label: 'Kenya', value: 'KE' },
  { label: 'Angola', value: 'AO' },
  { label: 'Cameroon', value: 'CM' },
  { label: 'Zambia', value: 'ZM' },
  { label: 'Burkina Faso', value: 'BF' },
  { label: 'Ghana', value: 'GH' },
  { label: 'Mali', value: 'ML' },
  { label: 'South Africa', value: 'ZA' },
];

const EditProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useUserStore();
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [selectedGenres, setSelectedGenres] = useState<string[]>(user?.genres || []);
  const [selectedMoods, setSelectedMoods] = useState<string[]>(user?.moods || []);
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(user?.avatar);
  const GENRE_OPTIONS = useGenreStore((state) => state.genreOptions);
  const MOOD_OPTIONS = useMoodStore((state: any) => state.moodOptions);
  const handleGenreToggle = (genre: string) => {
    setSelectedGenres(prev => 
      prev.includes(genre) 
        ? prev.filter(g => g !== genre)
        : [...prev, genre]
    );
  };

  const handleMoodToggle = (mood: string) => {
    setSelectedMoods(prev => 
      prev.includes(mood) 
        ? prev.filter(m => m !== mood)
        : [...prev, mood]
    );
  };

  const handleAvatarUpload = (fileInfo: any) => {
    if (fileInfo?.url) {
      setAvatarUrl(fileInfo.url);
    }
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    
    // 模拟 API 调用
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // 更新用户数据
    updateUser({
      name: values.name,
      avatar: avatarUrl,
      dateOfBirth: values.dateOfBirth,
      country: values.country,
      genres: selectedGenres,
      moods: selectedMoods,
      bio: values.bio,
      socialLinks: {
        instagram: values.instagram,
        twitter: values.twitter,
        spotify: values.spotify,
        apple: values.apple,
      },
    });
    
    setLoading(false);
    message.success('Profile updated successfully!');
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="edit-profile">
      {/* Header */}
      <div className="edit-profile__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Edit Profile</span>
        </button>
      </div>

      {/* Content */}
      <div className="edit-profile__content">
        <div className="edit-profile__form">
          <h2 className="form-section-title">Basic Information</h2>
          
          <Form 
            form={form} 
            onFinish={handleSubmit} 
            layout="vertical"
            initialValues={{
              name: user?.name,
              dateOfBirth: user?.dateOfBirth,
              country: user?.country,
              bio: user?.bio,
              instagram: user?.socialLinks?.instagram,
              twitter: user?.socialLinks?.twitter,
              spotify: user?.socialLinks?.spotify,
              apple: user?.socialLinks?.apple,
            }}
          >
            {/* 头像上传 */}
            <Form.Item label="Profile Picture">
              <div className="avatar-upload">
                <WkdUploadCore
                  accept="image/jpeg,image/png"
                  size={5 * 1024}
                  // ratio={1}
                  onChange={handleAvatarUpload}
                  onFaild={(error) => message.error(String(error))}
                >
                  <div className="avatar-upload__trigger">
                    {avatarUrl ? (
                      <img src={avatarUrl} alt="Avatar" className="avatar-upload__preview" />
                    ) : (
                      <div className="avatar-upload__placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                        </svg>
                        <span>Upload Photo</span>
                      </div>
                    )}
                  </div>
                </WkdUploadCore>
              </div>
            </Form.Item>

            {/* 艺术家名 */}
            <Form.Item 
              name="name" 
              label="Artist Name"
              rules={[{ required: true, message: 'Please enter your artist name' }]}
            >
              <Input placeholder="Enter your artist name" />
            </Form.Item>

            {/* 生日 */}
            <Form.Item name="dateOfBirth" label="Date of Birth">
              <Input type="date" placeholder="Select date" />
            </Form.Item>

            {/* 国家/地区 */}
            <Form.Item name="country" label="Country / Region">
              <Select 
                placeholder="Select your country" 
                options={COUNTRIES}
              />
            </Form.Item>

            {/* 社交媒体 */}
            <Form.Item label="Social Media Links">
              <div className="social-links">
                <Form.Item name="instagram" noStyle>
                  <Input placeholder="Instagram URL" />
                </Form.Item>
                <Form.Item name="twitter" noStyle>
                  <Input placeholder="Twitter URL" />
                </Form.Item>
                {/* <Form.Item name="spotify" noStyle>
                  <Input placeholder="Spotify Artist URL" />
                </Form.Item> */}
                <Form.Item name="tiktok" noStyle>
                  <Input placeholder="TikTok URL" />
                </Form.Item>
                <Form.Item name="youtube" noStyle>
                  <Input placeholder="YouTube URL" />
                </Form.Item>
                {/* <Form.Item name="apple" noStyle>
                  <Input placeholder="Apple Music URL" />
                </Form.Item> */}
              </div>
            </Form.Item>

            {/* 音乐类型 */}
            <Form.Item label="Genre">
              <div className="chips-container">
                {GENRE_OPTIONS.map((genre: any) => (
                  <Chips
                    key={genre.value}
                    selected={selectedGenres.includes(genre.value)}
                    onClick={() => handleGenreToggle(genre.value)}
                  >
                    {genre.label}
                  </Chips>
                ))}
              </div>
            </Form.Item>

            {/* 心情/氛围 */}
            <Form.Item label="Mood">
              <div className="chips-container">
                {MOOD_OPTIONS.map((mood: any) => (
                  <Chips
                    key={mood.value}
                    selected={selectedMoods.includes(mood.value)}
                    onClick={() => handleMoodToggle(mood.value)}
                  >
                    {mood.label}
                  </Chips>
                ))}
              </div>
            </Form.Item>

            {/* 个人简介 */}
            <Form.Item name="bio">
              <Input.TextArea label="Bio" rows={4} placeholder="Tell us about yourself" />
            </Form.Item>

            {/* 提交按钮 */}
            <Form.Item>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
                className="submit-btn"
              >
                Save Changes
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
